import { useState, useEffect } from 'react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Lightbulb, DollarSign, BookOpen, Users } from 'lucide-react';
import api from '@/utils/api';

const Entrepreneurship = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await api.get('/cms/pages/entrepreneurship');
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const defaultIcons = [Lightbulb, DollarSign, BookOpen, Users];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-slate-50 to-white flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-slate-50 to-white flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              {content?.title || 'Entrepreneurship'}
            </h1>
            <p className="text-xl text-teal-100 max-w-3xl">
              {content?.subtitle || 'Unlock your potential and explore new opportunities with our Entrepreneurship support.'}
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Introduction */}
          <div className="grid lg:grid-cols-2 gap-12 mb-16 items-stretch">
            <div className="flex flex-col justify-center">
              {content?.sections?.[0] && (
                <h2 className="text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit' }}>
                  {content.sections[0].title}
                </h2>
              )}
              {content?.hero_description ? (
                content.hero_description.split('\n\n').map((para, idx) => (
                  <p key={idx} className="text-slate-600 leading-relaxed mb-6">
                    {para}
                  </p>
                ))
              ) : (
                <p className="text-slate-600 leading-relaxed mb-6">
                  Do you want to start your own business but don't know where to begin?
                </p>
              )}
              {content?.sections?.[0]?.content && (
                content.sections[0].content.split('\n\n').map((para, idx) => (
                  <p key={`section-${idx}`} className="text-slate-600 leading-relaxed mb-6">
                    {para}
                  </p>
                ))
              )}
            </div>
            <div className="flex items-stretch">
              <img 
                src={content?.hero_image || '/assets/vvvsi-images/entrepreneurship.jpg'}
                alt="Entrepreneurship training"
                className="rounded-xl shadow-lg w-full h-full object-cover min-h-[380px]"
              />
            </div>
          </div>

          {/* Features */}
          {content?.features && content.features.length > 0 && (
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              {content.features.map((feature, index) => {
                const IconComponent = defaultIcons[index % defaultIcons.length];
                return (
                  <Card key={index} className="p-6 border-slate-200 hover:shadow-lg transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-teal-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                          {feature.title}
                        </h3>
                        <p className="text-slate-600 text-sm leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Gallery */}
          {content?.gallery_images && content.gallery_images.length > 0 && (
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center" style={{ fontFamily: 'Outfit' }}>
                Gallery
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {content.gallery_images.map((img, index) => (
                  <div key={index} className="aspect-[4/3] rounded-lg overflow-hidden">
                    <img 
                      src={img}
                      alt={`Entrepreneurship gallery ${index + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Entrepreneurship;
